import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import Login from './components/Login';
import SetupPage from './components/SetupPage';
import AdminPage from './components/AdminPage';
import PricingPage from './components/PricingPage';
import AppHeader from './components/AppHeader';
import KulkarniConsultingNote from './components/KulkarniConsultingNote';
import SearchTypeHome from './SearchTypeHome';
import IdentifyPropertyPage from './IdentifyPropertyPage';
import RentOrderPage from './RentOrderPage';
import SaleFlatOrderPage from './SaleFlatOrderPage';
import ReservationContractPage from './ReservationContractPage';
import PropertiesPage from './PropertiesPage';
import LvWorkspacePage from './LvWorkspacePage';
import { apiGet } from './api/client';

function App() {
  const { t, i18n } = useTranslation();
  const { user, ready, logout, getToken, passwordRecoveryInProgress } = useAuth();
  const [view, setView] = useState('home'); // 'home' | 'identify' | 'rentOrder' | 'saleFlatOrder' | 'reservationContract' | 'properties' | 'profile' | 'admin' | 'lvWorkspace'
  const [configChecked, setConfigChecked] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [hasPaidAccess, setHasPaidAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedPropertyForRent, setSelectedPropertyForRent] = useState(null);
  const [selectedPropertyForSale, setSelectedPropertyForSale] = useState(null);
  const [selectedPropertyForReservation, setSelectedPropertyForReservation] = useState(null);
  const [reservationHandoffToken, setReservationHandoffToken] = useState('');

  // When ready but no user, don't wait for config – show Login so user can sign in or use dev bypass
  useEffect(() => {
    if (ready && !user) {
      setConfigChecked(true);
    }
  }, [ready, user]);

  useEffect(() => {
    if (!user || !ready || passwordRecoveryInProgress) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/agent-config', getToken);
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.status === 401) {
            logout();
            return;
          }
          setHasConfig(res.ok && data.hasConfig === true);
          setHasPaidAccess(res.ok && data.hasPaidAccess === true);
          setIsAdmin(Boolean(data.isAdmin));
          setConfigChecked(true);
        }
      } catch {
        if (!cancelled) {
          setConfigChecked(true);
          setHasConfig(false);
          setHasPaidAccess(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, ready, getToken, logout, passwordRecoveryInProgress]);

  useEffect(() => {
    if (!user || !ready || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const requestedView = (params.get('view') || '').trim();
    const handoff = (params.get('reservation_handoff') || '').trim();
    if (requestedView === 'reservationContract' && handoff) {
      setReservationHandoffToken(handoff);
      setSelectedPropertyForReservation(null);
      setView('reservationContract');
    }
  }, [user, ready]);

  const mainNavItems = useMemo(() => {
    if (!user || !hasPaidAccess || !hasConfig || !isAdmin) return [];
    return [{ label: t('admin.menu'), onClick: () => setView('admin') }];
  }, [user, hasPaidAccess, hasConfig, isAdmin, t]);

  if (!ready) {
    return (
      <div className="app-shell-bg min-h-[100dvh] flex items-center justify-center text-brand-green/75 px-4 text-lg sm:text-xl font-semibold tracking-tight">
        Loading…
      </div>
    );
  }
  if (passwordRecoveryInProgress) {
    return <Login />;
  }
  if (!configChecked) {
    return (
      <div className="app-shell-bg min-h-[100dvh] flex items-center justify-center text-brand-green/75 px-4 text-lg sm:text-xl font-semibold tracking-tight">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Login />;
  }

  const displayName = user.full_name?.trim() || user.email || '';

  // Logged in but unpaid: show pricing (block profile setup until paid)
  if (!hasPaidAccess) {
    return (
      <div className="app-shell-bg flex flex-col w-full max-w-[100vw] overflow-x-hidden">
        <AppHeader
          title={t('app.title')}
          displayName={displayName}
          onLogout={logout}
          i18n={i18n}
          t={t}
          navItems={[]}
        />
        <main className="flex-1 w-full max-w-full overflow-x-hidden pb-[max(1rem,env(safe-area-inset-bottom))]">
          <PricingPage variant="postSignup" />
        </main>
      </div>
    );
  }

  // Paid but profile not set up
  if (!hasConfig) {
    return (
      <div className="app-shell-bg flex flex-col w-full max-w-[100vw] overflow-x-hidden">
        <AppHeader
          title={t('app.title')}
          displayName={displayName}
          onLogout={logout}
          i18n={i18n}
          t={t}
          navItems={[]}
        />
        <main className="flex-1 w-full max-w-full overflow-x-hidden p-4 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <SetupPage onDone={() => setHasConfig(true)} />
        </main>
        <KulkarniConsultingNote />
      </div>
    );
  }

  return (
    <div className="app-shell-bg flex flex-col w-full max-w-[100vw] overflow-x-hidden">
      <AppHeader
        title={t('app.title')}
        displayName={displayName}
        onProfileClick={() => setView('profile')}
        onLogout={logout}
        onHomeClick={() => setView('home')}
        i18n={i18n}
        t={t}
        navItems={mainNavItems}
      />
      <main className="flex-1 w-full max-w-full overflow-x-hidden px-1 sm:px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {view === 'home' && (
          <SearchTypeHome
            onSelect={(id) => {
              if (id === 'myProperties') setView('properties');
              if (id === 'lvWorkspace') setView('lvWorkspace');
            }}
          />
        )}
        {view === 'lvWorkspace' && (
          <LvWorkspacePage
            isAdmin={isAdmin}
            onBack={() => setView('home')}
            onSetupRequired={() => setHasConfig(false)}
            onOpenRentOrder={(prop) => {
              setSelectedPropertyForRent(prop);
              setSelectedPropertyForSale(null);
              setSelectedPropertyForReservation(null);
              setView('rentOrder');
            }}
            onOpenSaleOrder={(prop) => {
              setSelectedPropertyForSale(prop);
              setSelectedPropertyForRent(null);
              setSelectedPropertyForReservation(null);
              setView('saleFlatOrder');
            }}
            onOpenReservationContract={(prop) => {
              setSelectedPropertyForReservation(prop);
              setSelectedPropertyForSale(null);
              setSelectedPropertyForRent(null);
              setView('reservationContract');
            }}
          />
        )}
        {view === 'identify' && (
          <IdentifyPropertyPage onBack={() => setView('home')} />
        )}
        {view === 'rentOrder' && (
          <RentOrderPage
            key={
              selectedPropertyForRent
                ? `rent-${selectedPropertyForRent.kod_ku}-${selectedPropertyForRent.cislo_lv}`
                : 'rent-order'
            }
            onBack={() => {
              setSelectedPropertyForRent(null);
              setView('home');
            }}
            onSetupRequired={() => setHasConfig(false)}
            initialProperty={selectedPropertyForRent}
            onSwitchToSaleFlatOrder={(prop) => {
              setSelectedPropertyForSale(prop);
              setSelectedPropertyForRent(null);
              setView('saleFlatOrder');
            }}
            isAdmin={isAdmin}
          />
        )}
        {view === 'saleFlatOrder' && (
          <SaleFlatOrderPage
            key={
              selectedPropertyForSale
                ? `sale-${selectedPropertyForSale.kod_ku}-${selectedPropertyForSale.cislo_lv}`
                : 'sale-flat-order'
            }
            onBack={() => {
              setSelectedPropertyForSale(null);
              setView('home');
            }}
            onSetupRequired={() => setHasConfig(false)}
            initialProperty={selectedPropertyForSale}
            onSwitchToRentOrder={(prop) => {
              setSelectedPropertyForRent(prop);
              setSelectedPropertyForSale(null);
              setView('rentOrder');
            }}
            isAdmin={isAdmin}
          />
        )}
        {view === 'properties' && (
          <PropertiesPage
            onBack={() => setView('home')}
            onUseInRentOrder={(prop) => {
              setSelectedPropertyForRent(prop);
              setSelectedPropertyForSale(null);
              setSelectedPropertyForReservation(null);
              setView('rentOrder');
            }}
            onUseInSaleFlatOrder={(prop) => {
              setSelectedPropertyForSale(prop);
              setSelectedPropertyForRent(null);
              setSelectedPropertyForReservation(null);
              setView('saleFlatOrder');
            }}
            onUseInReservationContract={(prop) => {
              setSelectedPropertyForReservation(prop);
              setSelectedPropertyForSale(null);
              setSelectedPropertyForRent(null);
              setView('reservationContract');
            }}
            isAdmin={isAdmin}
          />
        )}
        {view === 'reservationContract' && (
          <ReservationContractPage
            key={
              selectedPropertyForReservation
                ? `reservation-${selectedPropertyForReservation.kod_ku}-${selectedPropertyForReservation.cislo_lv}`
                : reservationHandoffToken
                  ? `reservation-handoff-${reservationHandoffToken.slice(0, 16)}`
                  : 'reservation-contract'
            }
            onBack={() => {
              setSelectedPropertyForReservation(null);
              setReservationHandoffToken('');
              setView('home');
            }}
            onSetupRequired={() => setHasConfig(false)}
            initialProperty={selectedPropertyForReservation}
            isAdmin={isAdmin}
            handoffToken={reservationHandoffToken || null}
          />
        )}
        {view === 'profile' && (
          <SetupPage onDone={() => setView('home')} isUpdate />
        )}
        {view === 'admin' && isAdmin && (
          <AdminPage
            onBack={() => setView('home')}
            getToken={getToken}
          />
        )}
      </main>
      <KulkarniConsultingNote />
    </div>
  );
}

export default App;
